import { IdentityBrowserContractError, readBoundedJson } from "./kratos-browser-contract.js";
import {
  decodeOtherSessions,
  decodeOtherSessionsRevocation,
  decodeSessionError,
  otherSessionRevokeUrl,
} from "./kratos-session-contract.js";

function request(fetchImpl, url, { method = "GET", signal } = {}) {
  return fetchImpl(url, {
    method,
    credentials: "include",
    headers: { Accept: "application/json" },
    redirect: "error",
    signal,
  });
}

export async function loadIdentitySessionManagement({
  client,
  context,
  session,
  signal,
  fetchImpl = globalThis.fetch,
} = {}) {
  const [logout, sessionsResponse] = await Promise.all([
    client.createLogout({ signal }),
    request(fetchImpl, context.sessionsUrl, { signal }),
  ]);
  if (logout.kind === "signedOut" || sessionsResponse.status === 401) {
    return Object.freeze({ kind: "expired" });
  }
  if (sessionsResponse.status === 403) {
    return Object.freeze({
      kind: "reauthenticationRequired",
      ...decodeSessionError(
        await readBoundedJson(sessionsResponse, "Identity sessions"),
        context,
      ),
    });
  }
  let otherSessions = Object.freeze({ kind: "unavailable" });
  if (sessionsResponse.ok) {
    try {
      otherSessions = Object.freeze({
        kind: "ready",
        sessions: decodeOtherSessions(
          await readBoundedJson(sessionsResponse, "Identity sessions"),
        ),
      });
    } catch (error) {
      if (!(error instanceof IdentityBrowserContractError)) throw error;
      otherSessions = Object.freeze({
        kind: error.kind === "unsupported" ? "unsupported" : "invalid",
      });
    }
  }
  return Object.freeze({
    kind: "ready",
    session,
    logoutUrl: logout.logoutUrl,
    otherSessions,
  });
}

export async function revokeIdentitySessions({
  context,
  sessionId = "",
  signal,
  fetchImpl = globalThis.fetch,
} = {}) {
  const response = await request(
    fetchImpl,
    sessionId ? otherSessionRevokeUrl(context, sessionId) : context.sessionsUrl,
    { method: "DELETE", signal },
  );
  if (response.status === 401) return Object.freeze({ kind: "signedOut" });
  if (response.status === 403) {
    return Object.freeze({
      kind: "reauthenticationRequired",
      ...decodeSessionError(
        await readBoundedJson(response, "Identity session"),
        context,
      ),
    });
  }
  if (sessionId) {
    if (response.status !== 204) {
      throw new Error(`Session revocation returned HTTP ${response.status}`);
    }
  } else {
    if (!response.ok) {
      throw new Error(`Session revocation returned HTTP ${response.status}`);
    }
    decodeOtherSessionsRevocation(
      await readBoundedJson(response, "Identity session revocation"),
    );
  }
  return Object.freeze({ kind: "ready" });
}
